import _flowRight from 'lodash/flowRight';
import _uniqueId from 'lodash/uniqueId';
import { normalize } from 'normalizr';
import { differenceInMinutes } from 'date-fns';
import { getMatches } from '@homeservices/api-kit/search/matches/v1';

import { MATCHES_WITH_JOB_SCHEMA } from '@/store/modules/entities';
import { toSnakeCase } from '@/utils/to-snake-case';
import {
  withRequestLifecycle,
  REQUEST_LIFECYCLE,
} from '@/utils/request-lifecycle';
import {
  NEW_MATCH_STATUSES,
  CONTACTED_STATUSES,
  PASSED_STATUSES,
  IGNORED_STATUSES,
} from '@/constants/match-statuses';

const MUTATION_KEY = 'patchMatches';

const emptyArray = [];

const initialPartialState = {
  status: REQUEST_LIFECYCLE.UNINITIALIZED,
  error: null,
  result: emptyArray,
  count: 0,
  total: 0,
  hasMore: false,
};
function sortByTimeCreated (rawMatches) {
  const leads = Object.values(rawMatches).sort((a, b) => {
    const d1 = new Date(a.time_created);
    const d2 = new Date(b.time_created);
    if (d1.getTime() < d2.getTime()) {
      return 1;
    }
    if (d1.getTime() > d2.getTime()) {
      return -1;
    }
    return 0;
  });
  return leads.slice(0, 20);
}

export default {
  namespaced: true,
  state: {
    matchesKey: _uniqueId(),
  },
  getters: {
    matchesKey: state => state.matchesKey,
    getMatchesMetaByBucketName: state => (bucketName) => {
      const { result, ...meta } = state[bucketName] || initialPartialState;

      return meta;
    },
    getRawMatchesByBucketName: state => bucketName =>
      (state[bucketName] || initialPartialState).result,
    getAverageContactTime: (
      _,
      __,
      ___,
      { 'entities/proWorkOrdersMatches': rawMatches }
    ) => () => {
      const durations = sortByTimeCreated(rawMatches)
        .map((match) => {
          const { time_created, time_contacted } = match;

          return time_contacted
            ? {
              time_created,
              time_contacted,
            }
            : null;
        })
        .filter(Boolean);
      const wholeDuration = durations.reduce(
        (acc, { time_created, time_contacted }) =>
          acc + differenceInMinutes(new Date(time_contacted), new Date(time_created)),
        0
      );

      return durations.length
        ? Math.round(wholeDuration / durations.length)
        : 0;
    },
    getLeadResponseStats: (
      _,
      __,
      ___,
      { 'entities/proWorkOrdersMatches': rawMatches }
    ) => () => {
      const bucketMatches = sortByTimeCreated(rawMatches);
      return bucketMatches.reduce(
        ({ contacted, passed, ignored, completedAndHired }, match) => {
          const status = match.status.toLowerCase();

          return {
            contacted: CONTACTED_STATUSES.includes(status)
              ? contacted + 1
              : contacted,
            passed: PASSED_STATUSES.includes(status) ? passed + 1 : passed,
            ignored: IGNORED_STATUSES.includes(status) ? ignored + 1 : ignored,
            completedAndHired: [
              NEW_MATCH_STATUSES.HIRED,
              NEW_MATCH_STATUSES.COMPLETED,
            ].includes(status)
              ? completedAndHired + 1
              : completedAndHired,
          };
        },
        {
          contacted: 0,
          passed: 0,
          ignored: 0,
          completedAndHired: 0,
        }
      );
    },
    getLeadResponseRatio: (_, { getLeadResponseStats }) => (bucketName) => {
      const { contacted, passed, ignored } = getLeadResponseStats(bucketName);
      const total = contacted + passed + ignored;

      if (total > 0) {
        const value = (contacted + passed) / (contacted + passed + ignored);

        return Math.round(value * 100);
      }

      return 0;
    },
  },
  actions: {
    fetchMatches: _flowRight(
      withRequestLifecycle(MUTATION_KEY),
      () => async (
        context,
        { injected: { performFulfillment }, params = {} }
      ) => {
        const {
          data: { results, total },
        } = await getMatches(toSnakeCase(params));
        const { result, entities } = normalize(results, [
          MATCHES_WITH_JOB_SCHEMA,
        ]);

        return performFulfillment({
          total,
          result,
          entities,
        });
      }
    )(),
    resetMatches: ({ commit }, { bucketName }) =>
      commit('resetMatches', { meta: { bucketName } }),
  },
  mutations: {
    [MUTATION_KEY] (state, payload) {
      const { bucketName } = payload.meta;
      const partialState = state[bucketName] || initialPartialState;
      const result = [
        ...new Set([...partialState.result, ...(payload.result || emptyArray)]),
      ];

      state[bucketName] = {
        ...partialState,
        ...payload,
        result,
        count: result.length,
        hasMore: result.length < payload.total,
      };

      state.matchesKey = _uniqueId();
    },
    resetMatches (state, { meta }) {
      const { bucketName } = meta;

      state[bucketName] = initialPartialState;
      state[bucketName].meta = meta;

      state.matchesKey = _uniqueId();
    },
  },
};
